import { useState, useContext, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { ApiContext } from "../../../../context/MyContext";
import Button from "../../../../common/Button/Button";
import {
  fetchEmployees,
  modifyEmployeeRoleManagers,
} from "../../../../service/employeeService";
import { displayValue, getApiErrorMessage } from "../employeeListUtils";

const AssignManagerModal = ({ rowData, onClose, onSuccess, onToast }) => {
  const { post, get } = useContext(ApiContext);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(true);

  const employeeRoleId = rowData?.employee_role_id ?? null;
  const employeeName = rowData?.name ?? "Employee";
  const roleTitle = rowData?.role ?? "";
  const currentManager = displayValue(rowData?.manager_name);

  useEffect(() => {
    let cancelled = false;

    const loadManagers = async () => {
      try {
        setLoadingManagers(true);
        const { employees } = await fetchEmployees(get);
        const list = (employees || [])
          .filter(
            (emp) =>
              emp.user_id !== rowData?.user_id && emp.is_verified_employee
          )
          .flatMap((emp) => {
            const roles = emp.current_roles || emp.roles || [];
            return roles
              .map((r) => {
                const roleObj = r.role ?? r;
                const managerEmployeeRoleId =
                  r.employee_role_id ?? roleObj?.employee_role_id;
                if (!managerEmployeeRoleId) return null;
                return {
                  user_id: emp.user_id,
                  name:
                    `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
                    emp.name,
                  manager_employee_role_id: managerEmployeeRoleId,
                  role_title: r.role_title ?? roleObj?.title ?? "N/A",
                };
              })
              .filter(Boolean);
          });

        if (cancelled) return;
        setManagers(list);
        setSelectedManager(list[0] || null);
      } catch (err) {
        console.error("Error loading managers:", err);
        if (!cancelled) {
          onToast("Failed to load managers", "error");
          setManagers([]);
          setSelectedManager(null);
        }
      } finally {
        if (!cancelled) setLoadingManagers(false);
      }
    };

    loadManagers();
    return () => {
      cancelled = true;
    };
  }, [get, onToast, rowData?.user_id]);

  const handleAssign = async () => {
    if (!employeeRoleId) {
      onToast("This role cannot have a manager assigned.", "error");
      return;
    }
    if (!selectedManager?.manager_employee_role_id) {
      onToast("Please select a manager.", "error");
      return;
    }

    try {
      setLoading(true);
      await modifyEmployeeRoleManagers(post, {
        manager_assignments: [
          {
            operation: "ADD",
            employee_role_id: employeeRoleId,
            manager_role_id: selectedManager.manager_employee_role_id,
          },
        ],
      });
      onToast("Manager assigned successfully.", "success");
      onSuccess();
    } catch (err) {
      onToast(getApiErrorMessage(err, "Failed to assign manager"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-orange-primary bg-opacity-20 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-lg w-[500px] min-w-[400px] max-w-[600px] border border-orange-200"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold mb-2">Update Manager</h2>
        <p className="text-sm text-gray-600 mb-1">
          {employeeName}
          {roleTitle ? ` · ${roleTitle}` : ""}
        </p>
        <p className="text-xs text-gray-500 mb-4">
          Manager is assigned to this role only. One person with multiple roles
          can have different managers per role.
        </p>
        {currentManager && (
          <p className="text-sm text-gray-500 mb-4">
            Current manager: {currentManager}
          </p>
        )}

        <label className="block text-gray-700 font-semibold mb-2">
          Select Manager * (search by name or role)
        </label>
        {loadingManagers ? (
          <p className="text-sm text-gray-500 py-2 mb-4">Loading managers...</p>
        ) : (
          <Autocomplete
            value={selectedManager}
            onChange={(_, newValue) => setSelectedManager(newValue)}
            options={managers}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.name ?? ""} · ${option.role_title ?? ""}`.trim() ||
                  "—"
            }
            isOptionEqualToValue={(option, value) =>
              option?.manager_employee_role_id === value?.manager_employee_role_id
            }
            filterOptions={(options, { inputValue }) => {
              const q = (inputValue || "").trim().toLowerCase();
              if (!q) return options;
              return options.filter(
                (m) =>
                  m.name?.toLowerCase().includes(q) ||
                  m.role_title?.toLowerCase().includes(q)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name or role..."
                size="small"
                variant="outlined"
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={`${option.user_id}-${option.manager_employee_role_id}`}
              >
                <span className="font-medium">{option.name}</span>
                <span className="text-gray-500 ml-2">· {option.role_title}</span>
              </li>
            )}
            noOptionsText="No managers match your search"
            sx={{ mb: 2 }}
          />
        )}

        <div className="flex gap-3 justify-end">
          <Button
            text="Cancel"
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
            onClick={onClose}
          />
          <Button
            text={loading ? "Updating…" : "Update Manager"}
            className="bg-orange-primary hover:bg-orange-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
            onClick={handleAssign}
            disabled={
              loading ||
              loadingManagers ||
              !selectedManager?.manager_employee_role_id ||
              !employeeRoleId
            }
          />
        </div>
      </div>
    </div>
  );
};

export default AssignManagerModal;
